import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { SearchApiServiceInterface } from "../interfaces/search-api-service.interface";
import { SearchParametersQuery, SearchResult, SearchResultItem } from "../../../../model";
import { ViewerConfigurationService } from "../../../../../viewer-components/services/viewer-configuration.service";

/**
 * Legacy implementation for the embeddings API
 * Adapts the final SearchParametersQuery format to the current GET-based endpoint
 */
@Injectable({
  providedIn: "root",
})
export class EmbeddingsApiService implements SearchApiServiceInterface {
  private readonly searchEndpoint = "/embeddings/api/search/text/model";

  public get httpHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: "Bearer " + this.token,
      Accept: "application/json",
    });
  }

  public get apiUrl(): string {
    return this.viewerConfigurationService.getViewerConfiguration().compoundApiUrl;
  }

  public get token(): string {
    return this.viewerConfigurationService.getViewerConfiguration().token;
  }

  constructor(
    private viewerConfigurationService: ViewerConfigurationService,
    private http: HttpClient,
  ) {}

  /**
   * Performs a search query by adapting SearchParametersQuery to legacy format
   * @param params Search parameters in final API format
   * @returns Observable containing the search results
   */
  search(params: SearchParametersQuery): Observable<SearchResult> {
    // Convert SearchParametersQuery to legacy GET parameters
    const legacyParams = this.convertToLegacyParams(params);

    // Create HttpParams from the converted parameters
    let httpParams = new HttpParams();
    Object.entries(legacyParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, value);
      }
    });

    const url = this.apiUrl + this.searchEndpoint;

    // Make GET request with legacy query params
    return this.http.get<any[]>(url, { params: httpParams, headers: this.httpHeaders }).pipe(
      map((items) => this.mapToSearchResult(items, params)),
      catchError((error) => {
        console.error("Legacy Search API error:", error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * This legacy implementation is never wired to SEARCH_API_SERVICE (see
   * main-layout.component.ts, which always provides ApiSearchService), so geometry
   * validation always resolves to true here instead of calling the legacy endpoint.
   * @returns Observable resolving to true
   */
  validateGeometry(): Observable<boolean> {
    // In the legacy implementation, we don't have a dedicated endpoint for geometry validation,
    // so we simply return true to indicate that the geometry is valid.
    return of(true);
  }

  /**
   * Converts SearchParametersQuery to legacy API parameters
   * @private
   */
  private convertToLegacyParams(params: SearchParametersQuery): Record<string, string> {
    const legacyParams: Record<string, string> = {
      // Fixed parameters for current API compatibility
      model: "Cohere v4",
      db_model: "Cohere v4",
      zoom: "20",
      tile_size: "512",
      comparator: "<=>",
      image_type: "ortho",
      size: params["page-size"]?.toString() || params["total-records"]?.toString() || "20",
      page: params.page?.toString() || "1",
    };

    // Add search-specific parameters based on search type
    this.addSearchSpecificParams(legacyParams, params);

    return legacyParams;
  }

  /**
   * Add search-specific parameters to legacy params object based on search type
   * @private
   */
  private addSearchSpecificParams(legacyParams: Record<string, string>, params: SearchParametersQuery): void {
    // Determine search type and add appropriate parameters
    if (params["search-text"]) {
      // Text search - directly supported
      legacyParams.query = params["search-text"];
    } else if (params["search-url"]) {
      // Image URL search - fallback to text search until endpoint is ready
      legacyParams.query = `[Image Search: ${params["search-url"]}]`;
    } else if (params["search-image"]) {
      // Base64 image search - fallback to text search until endpoint is ready
      legacyParams.query = "[Image Search: Uploaded Image]";
    } else if (params["search-location"]) {
      // Location search - fallback to text search until endpoint is ready
      legacyParams.query = `[Location Search: ${params["search-location"]}]`;
    }

    // Handle WKT geometry if provided
    if (params.wkt && params["buffer-meter"]) {
      // For now, include in query text until proper geo endpoint is available
      const wktInfo = `[Geometry Filter: ${params.wkt.substring(0, 50)}...]`;
      if (legacyParams.query) {
        legacyParams.query += ` ${wktInfo}`;
      } else {
        legacyParams.query = wktInfo;
      }
    }
  }

  /**
   * Maps legacy API response to SearchResult format
   * @private
   */
  private mapToSearchResult(items: any[], params: SearchParametersQuery): SearchResult {
    const mappedItems: SearchResultItem[] = items.map((item, index) => {
      // Extract tile coordinates from path

      return {
        quadkey: item.quadkey || `temp_${index}`,
        similarity: item.similarity || 0,
        geometry: item.geometry || "",
        tileUrl: item.tile_path || "",
        productType: item["orientation-source"] || item["source.product-type"] || "",
        key: item.quadkey || `temp_${index}`,
        metadata: {
          model: item.model,
          image_type: item.image_type,
          tile_size: item.tile_size,
          zoom: item.zoom,
        },
      };
    });

    return {
      total: mappedItems.length, // Legacy API doesn't provide total count
      items: mappedItems,
      metadata: {
        page: params.page || 1,
        pageSize: params["page-size"] || params["total-records"] || 20,
        searchType: this.determineSearchTypeFromParams(params),
        apiVersion: "legacy-embeddings",
      },
    };
  }

  /**
   * Determines search type from parameters
   * @private
   */
  private determineSearchTypeFromParams(params: SearchParametersQuery): string {
    if (params["search-text"]) return "text";
    if (params["search-url"]) return "image-url";
    if (params["search-image"]) return "image-data";
    if (params["search-location"]) return "location";
    return "text";
  }
}
